import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { useRecognitions, useRecognitionStats, useRecognitionTypes, RecognitionFilters } from '../../hooks/useRecognition';
import { useBrgList } from '../../hooks/useBrgList';
import { useRecognitionStatuses } from '../../hooks/useRecognitionStatuses';
import { useAuth } from '../../auth/useAuth';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { FilterSelect } from '../../components/shared/FilterSelect';
import SubmitRecognitionModal from './SubmitRecognitionModal';
import ReviewRecognitionModal from './ReviewRecognitionModal';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface RecognitionRow {
  recognitionId: number;
  recipientName: string;
  brgName: string;
  eventTitle: string;
  recognitionTypeLabel: string;
  pointsSnapshot: number;
  submittedAt: string;
  recognitionStatusId: number;
}

export default function RecognitionRequestsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = auth?.roles?.includes('BRG.Admin') || false;
  const isLeader = auth?.roles?.includes('BRG-Lead') || false;

  // Block access for non-admin/non-leader
  if (!isAdmin && !isLeader) {
    navigate('/forbidden');
    return null;
  }

  // State for filters & modals
  const [filters, setFilters] = useState<RecognitionFilters>({});
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedRecognitionId, setSelectedRecognitionId] = useState<number | null>(null);
  const [successBanner, setSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Hooks
  const { data: recognitions, isLoading: isLoadingRecognitions, error: recognitionsError } = useRecognitions(filters);
  const { data: stats, isLoading: isLoadingStats } = useRecognitionStats();
  const { data: types } = useRecognitionTypes();
  const { data: brgs } = useBrgList();
  const { data: statuses } = useRecognitionStatuses();

  const handleFilterChange = (key: keyof RecognitionFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleSubmissionSuccess = () => {
    setShowSubmitModal(false);
    setSuccessBanner(true);
    setSuccessMessage('Recognition request was successfully submitted.');
    const timer = setTimeout(() => {
      setSuccessBanner(false);
    }, 5000);
    return () => clearTimeout(timer);
  };

  const handleApprovalSuccess = (message: string) => {
    setSuccessMessage(message);
    setSuccessBanner(true);
    setSelectedRecognitionId(null);
    const timer = setTimeout(() => {
      setSuccessBanner(false);
    }, 5000);
    return () => clearTimeout(timer);
  };

  const hasFiltersApplied = Object.values(filters).some(v => v !== undefined);
  const isEmpty = !isLoadingRecognitions && (!recognitions || recognitions.length === 0);

  // Loading state
  if (isLoadingRecognitions || isLoadingStats) {
    return <LoadingSpinner message="Loading recognition requests..." fullPage={true} />;
  }

  // Error state
  if (recognitionsError) {
    return <ErrorMessage message={`Failed to load recognitions: ${recognitionsError.message}`} />;
  }

  // Get color for a recognition type from the types data
  const getTypeColor = (typeLabel: string | undefined) => {
    if (!typeLabel || !types) return { bg: '#e3f2fd', text: '#1565c0' };
    // Remove points/dollar suffix if present (e.g., "Shine On — 600 pts / $30" -> "Shine On")
    const clean = typeLabel.split(' — ')[0].split(' - ')[0];
    const typeData = types.find(t => t.label === clean);
    if (typeData && typeData.backgroundColor && typeData.textColor) {
      return { bg: typeData.backgroundColor, text: typeData.textColor };
    }
    return { bg: '#e3f2fd', text: '#1565c0' };
  };

  // Helper function to get status name by ID
  const getStatusName = (statusId: number): string => {
    if (!statuses || statuses.length === 0) {
      // Fallback to hardcoded names
      if (statusId === 1) return 'Pending';
      if (statusId === 2) return 'Approved';
      if (statusId === 3) return 'Denied';
      return 'Unknown';
    }
    const status = statuses.find(s => s.recognitionStatusId === statusId);
    return status?.recognitionStatusName || 'Unknown';
  };

  // Note: Styles defined below but currently using AG Grid component instead
  // Keeping for potential future use or reference
  // const thStyle: React.CSSProperties = { ... }
  // const tdStyle: React.CSSProperties = { ... }

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#1a3bcc',
    color: '#fff',
    border: 'none',
    padding: '7px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
  };

  const statusBadgeStyle = (statusId: number): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'inline-block',
      padding: '2px 9px',
      borderRadius: '10px',
      fontSize: '10px',
      fontWeight: 700,
    };
    
    if (!statuses || statuses.length === 0) {
      // Fallback to hardcoded colors if statuses not loaded
      if (statusId === 2) {
        return { ...baseStyle, backgroundColor: '#d4edda', color: '#1a7c3a' };
      } else if (statusId === 3) {
        return { ...baseStyle, backgroundColor: '#f8d7da', color: '#a01a23' };
      } else {
        return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' };
      }
    }

    // Find the matching status and use its colors
    const statusData = statuses.find(s => s.recognitionStatusId === statusId);
    if (statusData) {
      return { ...baseStyle, backgroundColor: statusData.backgroundColor, color: statusData.textColor };
    }
    
    // Default fallback
    return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404' };
  };

  // Determine banner styling based on message content
  const getBannerStyles = () => {
    if (successMessage.includes('denied')) {
      return {
        backgroundColor: '#f8d7da',
        border: '1px solid #f1a9b5',
        color: '#a01a23',
        icon: '✕',
      };
    }
    // Yellow/warning for budget sync failures
    if (successMessage.includes('Budget Tracking dashboard sync failed')) {
      return {
        backgroundColor: '#fff3cd',
        border: '1px solid #ffe69c',
        color: '#856404',
        icon: '⚠',
      };
    }
    // Green for approved or submitted
    return {
      backgroundColor: '#d4edda',
      border: '1px solid #a3d4b0',
      color: '#1a7c3a',
      icon: '✓',
    };
  };

  const bannerStyles = getBannerStyles();

  return (
    <div style={{ width: '100%', padding: '20px 80px', maxWidth: 'none' }}>
      {/* Success Banner */}
      {successBanner && (
        <div
          style={{
            backgroundColor: bannerStyles.backgroundColor,
            border: `1px solid ${bannerStyles.border}`,
            borderRadius: '4px',
            padding: '12px 16px',
            marginBottom: '18px',
            fontSize: '13px',
            fontWeight: 700,
            color: bannerStyles.color,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '16px' }}>{bannerStyles.icon}</span>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Filter Card */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '6px',
          padding: '14px 18px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          flexWrap: 'wrap',
        }}
      >
        {/* BRG Filter */}
        <FilterSelect
          label="BRG:"
          value={filters.brgId || ''}
          onChange={(e) => handleFilterChange('brgId', e.target.value ? parseInt(e.target.value) : undefined)}
        >
          <option key="all-brgs" value="">All BRGs</option>
          {brgs?.map(b => (
            <option key={b.brgId} value={b.brgId}>{b.name}</option>
          ))}
        </FilterSelect>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#ddd' }}></div>

        {/* Status Filter */}
        <FilterSelect
          label="Status:"
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option key="all-statuses" value="">All Statuses</option>
          {statuses?.map(s => (
            <option key={s.recognitionStatusId} value={s.recognitionStatusId}>{s.recognitionStatusName}</option>
          ))}
        </FilterSelect>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#ddd' }}></div>

        {/* Type Filter */}
        <FilterSelect
          label="Recognition Type:"
          value={filters.recognitionTypeId || ''}
          onChange={(e) => handleFilterChange('recognitionTypeId', e.target.value ? parseInt(e.target.value) : undefined)}
        >
          <option key="all-types" value="">All Types</option>
          {types?.map(t => (
            <option key={t.recognitionTypeId} value={t.recognitionTypeId}>
              {t.label}
            </option>
          ))}
        </FilterSelect>

        {/* Submit Button */}
        <button
          onClick={() => setShowSubmitModal(true)}
          style={{
            ...buttonStyle,
            marginLeft: 'auto',
            height: '32px',
          }}
        >
          + Submit Recognition
        </button>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '18px' }}>
        {/* Total YTD - Highlighted */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#1a3bcc',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', opacity: 0.85 }}>Total Recognitions YTD</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1 }}>{stats?.totalYtd || 0}</div>
          <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.85 }}>Across all BRGs· FY {new Date().getFullYear()}</div>
        </div>

        {/* Approved */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', color: '#888' }}>{getStatusName(2)}</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1, color: '#1a7c3a' }}>
            {stats?.approvedCount || 0}
          </div>
          <div style={{ fontSize: '10px', marginTop: '4px', color: '#999' }}>
            {stats?.approvalRatePercent || 0}% approval rate
          </div>
        </div>

        {/* Pending */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', color: '#888' }}>{getStatusName(1)}</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1, color: '#856404' }}>{stats?.pendingCount || 0}</div>
          <div style={{ fontSize: '10px', marginTop: '4px', color: '#999' }}>Awaiting review</div>
        </div>

        {/* Denied */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '6px',
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', color: '#888' }}>{getStatusName(3)}</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1, color: '#a01a23' }}>
            {stats?.deniedCount || 0}
          </div>
          <div style={{ fontSize: '10px', marginTop: '4px', color: '#999' }}>
            {stats?.denialRatePercent || 0}% of total
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '6px',
          padding: '16px 20px',
          marginBottom: '16px',
        }}
      >
        {isEmpty && !hasFiltersApplied ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <p style={{ fontSize: '14px', marginBottom: '16px' }}>No recognition requests have been submitted yet.</p>
            <button
              onClick={() => setShowSubmitModal(true)}
              style={{ ...buttonStyle, marginBottom: '12px' }}
            >
              Submit the first recognition
            </button>
          </div>
        ) : isEmpty && hasFiltersApplied ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <p style={{ fontSize: '14px', marginBottom: '16px' }}>No recognition requests match the selected filters.</p>
            <button
              onClick={handleClearFilters}
              style={{
                color: '#1a3bcc',
                backgroundColor: 'transparent',
                padding: '6px 12px',
                border: '1px solid #1a3bcc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#111', marginBottom: '10px' }}>
              Recent Moment Maker Requests
            </div>
            <RecognitionGrid
              recognitions={recognitions}
              statuses={statuses}
              onRowClick={(recognitionId: number) => setSelectedRecognitionId(recognitionId)}
              getTypeColor={getTypeColor}
              statusBadgeStyle={statusBadgeStyle}
            />
          </>
        )}
      </div>

      {/* Modals */}
      {showSubmitModal && (
        <SubmitRecognitionModal
          onClose={() => setShowSubmitModal(false)}
          onSuccess={handleSubmissionSuccess}
        />
      )}

      {selectedRecognitionId && (
        <ReviewRecognitionModal
          recognitionId={selectedRecognitionId}
          onClose={() => setSelectedRecognitionId(null)}
          onApprovalSuccess={handleApprovalSuccess}
        />
      )}
    </div>
  );

  // AG Grid Component (nested inside main component)
  function RecognitionGrid({ recognitions, statuses, onRowClick, getTypeColor, statusBadgeStyle }: any) {
    const gridRef = useRef<AgGridReact>(null);

    // Custom CSS for AG Grid styling (matching Budget Dashboard style)
    const customGridStyles = `
      .ag-theme-quartz {
        --ag-header-background-color: #1a3bcc;
        --ag-header-foreground-color: #fff;
        --ag-header-cell-text-color: #fff;
        --ag-header-cell-padding: 8px 10px;
        --ag-header-cell-hover-background-color: #1530a8;
        --ag-header-cell-moving-background-color: #1530a8;
        --ag-font-size: 11px;
        --ag-cell-horizontal-padding: 10px;
        --ag-cell-vertical-padding: 8px;
        --ag-row-height: 32px;
        --ag-row-border-color: #f0f0f0;
        background-color: #ffffff;
        border: none;
        border-radius: 0;
      }
      
      .ag-theme-quartz .ag-header {
        font-weight: 700;
        font-size: 11px;
        height: 32px;
        background-color: #1a3bcc;
      }
      
      .ag-theme-quartz .ag-header-cell {
        background-color: #1a3bcc !important;
        color: #fff !important;
        border-right: 1px solid #1530a8;
        font-weight: 700;
      }
      
      .ag-theme-quartz .ag-header-cell:last-child {
        border-right: none;
      }
      
      .ag-theme-quartz .ag-row {
        border-bottom: 1px solid #f0f0f0;
        height: 32px;
        background-color: #ffffff;
      }
      
      .ag-theme-quartz .ag-row:last-child {
        border-bottom: none;
      }
      
      .ag-theme-quartz .ag-row:hover {
        background-color: #f7f9ff !important;
      }
      
      .ag-theme-quartz .ag-cell {
        font-size: 11px;
        color: #333;
        padding: 8px 10px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        display: flex;
        align-items: center;
      }
      
      .ag-theme-quartz .ag-root {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      }
      
      .ag-theme-quartz .ag-root-wrapper {
        border: none;
      }
      
      .ag-theme-quartz .ag-center-cols-viewport {
        overflow-x: auto;
      }
    `;

    const columnDefs: ColDef<RecognitionRow>[] = useMemo(
      () => [
        {
          field: 'recipientName',
          headerName: 'Recipient',
          flex: 1,
          minWidth: 120,
        },
        {
          field: 'brgName',
          headerName: 'BRG',
          flex: 1,
          minWidth: 120,
        },
        {
          field: 'eventTitle',
          headerName: 'Linked Event',
          flex: 1.2,
          minWidth: 150,
          cellRenderer: (params: any) => {
            const eventTitle = params.value || '—';
            return (
              <div title={eventTitle} style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {eventTitle}
              </div>
            );
          },
        },
        {
          field: 'recognitionTypeLabel',
          headerName: 'Type',
          flex: 1,
          minWidth: 110,
          cellRenderer: (params: any) => {
            const typeLabel = params.value;
            const typeColor = getTypeColor(typeLabel);
            return (
              <span
                style={{
                  backgroundColor: typeColor.bg,
                  color: typeColor.text,
                  padding: '2px 9px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: 700,
                  display: 'inline-block',
                }}
              >
                {typeLabel ? typeLabel.split(' - ')[0] : '—'}
              </span>
            );
          },
        },
        {
          field: 'pointsSnapshot',
          headerName: 'Points',
          flex: 0.8,
          minWidth: 80,
          valueFormatter: (params: any) => (params.value ? params.value.toLocaleString() : '0'),
          cellStyle: { fontWeight: 700 },
        },
        {
          field: 'submittedAt',
          headerName: 'Date Submitted',
          flex: 1,
          minWidth: 110,
          valueFormatter: (params: any) =>
            params.value
              ? new Date(params.value).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
              : '—',
        },
        {
          field: 'recognitionStatusId',
          headerName: 'Status',
          flex: 0.9,
          minWidth: 100,
          cellRenderer: (params: any) => {
            const statusId = params.value;
            const statusName = statuses?.find((s: any) => s.recognitionStatusId === statusId)?.recognitionStatusName || 'Unknown';
            return (
              <span style={statusBadgeStyle(statusId)}>
                {statusName}
              </span>
            );
          },
        },
      ],
      [statuses, getTypeColor, statusBadgeStyle]
    );

    const gridOptions: GridOptions<RecognitionRow> = {
      domLayout: 'normal',
      rowHeight: 32,
      headerHeight: 32,
      suppressColumnVirtualisation: false,
      suppressPaginationPanel: false,
      paginationPageSize: 10,
      suppressCellFocus: true,
      suppressMovableColumns: true,
    };

    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '6px',
          overflow: 'hidden',
          height: '400px',
        }}
      >
        <style>{customGridStyles}</style>
        <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
          <AgGridReact<RecognitionRow>
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={recognitions}
            gridOptions={gridOptions}
            defaultColDef={{
              resizable: false,
              sortable: false,
              filter: false,
            }}
            onRowClicked={(event) => onRowClick(event.data!.recognitionId)}
            rowStyle={{
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
          />
        </div>
      </div>
    );
  }
}
